<template>
  <div>
    <!-- Current cover has a partner -->
    <SectionSeparatorWrapper headerText="Partner" headerRequiredFieldsText="indicates required field" v-if="oldCompositionHasPartner" :separator="false">
      <h3 class="h6 sub-heading-underlined">Partner detail</h3>
      <FormField :helperText="informationReferenceData.additionalInformationUpdateDetails.informationText" :noLabelOverride="true">
        <MemberTag
          :firstName="partner.existing.firstName"
          :lastName="partner.existing.lastName ? partner.existing.lastName : '&#8212;'"
          :showLastName="true"
          relationship="Partner"
        />
      </FormField>
      <FormField
        :labelText="informationReferenceData.additionalInformationKeepPartner.label"
        :helperText="informationReferenceData.additionalInformationKeepPartner.informationText"
        style="margin-bottom: 3rem"
      >
        <ul class="radio-group large">
          <li v-for="option in displayRadioOptions" :key="`veteran-card-${option.display}`">
            <input type="radio" :id="`keep-adult-${option.display}`" v-model.lazy="partner.existing.keepAdult" :value="option.returnValue" @change="!partner.existing.keepAdult" />
            <label :for="`keep-adult-${option.display}`">{{ option.display }}</label>
          </li>
        </ul>
      </FormField>
      <FormField v-if="!partner.existing.keepAdult">
        <Alert alertType="info" class="inline-message">
          <div>
            <p>This adult will no longer be covered on this policy.</p>
          </div>
        </Alert>
      </FormField>

      <!-- If partner holds policy eligibility and is being removed from the policy -->
      <FormField v-if="!partner.existing.keepAdult && !isPolicyHolderEligible" class="form-section" :required="!isPolicyHolderEligible">
        <Alert alertType="info" class="inline-message">
          <div>
            <p>
              Removing this adult will require eligibility to be updated for the policy. Please discuss the options available and complete this change through the change of cover
              flow.
            </p>
          </div>
        </Alert>
        <input
          type="checkbox"
          id="acknowledgedEligibilityChange"
          name="acknowledgedEligibilityChange"
          v-model="partner.existing.acknowledgedEligibilityChange"
          @change="v$.partner.existing.acknowledgedEligibilityChange.$touch()"
          @keyup.enter.space="partner.existing.acknowledgedEligibilityChange = !partner.existing.acknowledgedEligibilityChange"
        />
        <label for="acknowledgedEligibilityChange">
          {{ informationReferenceData.partnerRemovalAcknowledgementText.informationText }}
        </label>
      </FormField>
      <Alert v-if="mustHavePartnerValidationFailed" :message="ENFORCE_PARTNER_ERROR_MESSAGE" :isPlainText="false"></Alert>

      <!-- If partner holds rebate eligibility and is being removed from the policy -->
      <FormField v-if="isRebateApplicantBeingTerminated">
        <Alert alertType="error" class="inline-message" :message="PARTNER_REBATE_HOLDER_ERROR_MESSAGE"></Alert>
      </FormField>

      <FormField
        v-if="!partner.existing.keepAdult && !isRebateApplicantBeingTerminated"
        class="form-section"
        labelText="Reason for termination"
        :required="!partner.existing.keepAdult"
      >
        <DropdownSelect
          :items="terminationReasonsMemberList"
          v-model="partner.existing.terminationReason"
          name="partner-termination-reason"
          :id="`partner-${partner.existing.terminationReason}`"
          :initialSelect="getDropdownOption(terminationReasonsMemberList, partner.existing.terminationReason, 'Please select a reason')"
        />
      </FormField>
      <FormField
        v-if="!partner.existing.keepAdult && transferToFundValues.includes(partner.existing.terminationReason)"
        class="form-section"
        labelText="New fund name"
        :required="!partner.existing.keepAdult && transferToFundValues.includes(partner.existing.terminationReason)"
      >
        <DropdownSelect
          :items="fundNamesTerminated"
          v-model="partner.existing.fundName"
          name="partner-fund-name"
          :initialSelect="getDropdownOption(fundNamesTerminated, partner.existing.fundName, 'Please select a health fund')"
        />
      </FormField>
    </SectionSeparatorWrapper>

      <!-- New partner section -->
      <SectionSeparatorWrapper class="field" v-if="showNewPartnerSection && compositionAllowsPartner && !isRebateApplicantBeingTerminated" :separator="false">
        <!-- Duplicate person check loading spinner -->
        <div v-if="apiStatus.duplicatePersonCheck.loading" class="loading-screen">
          <div class="form-grid">
            <Spinner />
          </div>
        </div>

        <h3 class="h6 sub-heading-underlined">Adding a new partner?</h3>
        <FormField labelText="If a new partner is being added, capture the details for the new adult being added to the membership">
          <ul class="radio-group large">
            <li v-for="option in displayRadioOptions" :key="`add-new-partner-${option.display}`">
              <input type="radio" :id="`new-partner-${option.display}`" v-model="partner.newPartner.add" :value="option.returnValue" />
              <label :for="`new-partner-${option.display}`">{{ option.display }}</label>
            </li>
          </ul>
          <br />
        </FormField>

        <!-- New partner details section -->
        <template v-if="partner.newPartner.add && showNewPartnerSection && compositionAllowsPartner">
          <!-- Name -->
            <div class="form-section">
              <FormField
                labelText="First name"
                placeholder="e.g. John"
                type="text"
                id="new-partner-first-name"
                v-model.lazy="v$.partner.newPartner.firstName.$model"
                :error="v$.partner.newPartner.firstName.$error"
                :errorMessage="getNameErrorMessage('firstName')"
                :disabled="partner.newPartner.isDuplicate || apiStatus.duplicatePersonCheck.loading"
                @input="searchDebounce(runDuplicatePersonSearch)"
                @blur="runDuplicatePersonSearch()"
                required
              />
            </div>
            <div class="form-section">
              <FormField
                labelText="Middle name"
                placeholder="e.g. Fred"
                type="text"
                id="new-partner-middle-name"
                v-model.lazy="v$.partner.newPartner.middleName.$model"
                :disabled="partner.newPartner.isDuplicate || apiStatus.duplicatePersonCheck.loading"
                :error="v$.partner.newPartner.middleName.$error"
                :errorMessage="getNameErrorMessage('middleName')"
                :required="false"
              />
            </div>
            <div class="form-section">
              <FormField
                id="new-partner-last-name"
                labelText="Last name"
                placeholder="e.g. Citizen"
                type="text"
                required
                v-model.lazy="v$.partner.newPartner.lastName.$model"
                :error="v$.partner.newPartner.lastName.$error"
                :errorMessage="getNameErrorMessage('lastName')"
                :disabled="partner.newPartner.isDuplicate || apiStatus.duplicatePersonCheck.loading"
                @input="searchDebounce(runDuplicatePersonSearch)"
                @blur="runDuplicatePersonSearch()"
              />
            </div>
          <!-- Sex -->
          <FormField labelText="Sex" :helperText="informationReferenceData.sex.helpText" class="form-section">
            <ul class="radio-group large">
              <li v-for="sex in informationReferenceData.sex.options" :key="sex.returnValue">
                <input
                  :id="sex.returnValue"
                  :value="sex.returnValue"
                  :disabled="partner.newPartner.isDuplicate || apiStatus.duplicatePersonCheck.loading"
                  type="radio"
                  v-model="v$.partner.newPartner.gender.$model"
                  @change="runDuplicatePersonSearch()"
                />
                <label :for="sex.returnValue">{{ sex.display }}</label>
              </li>
            </ul>
          </FormField>
          <!-- DOB -->
          <FormField
            id="new-partner-dob"
            labelText="Date of birth"
            class="form-section half-width"
            placeholder="DD / MM / YYYY"
            v-maska="'## / ## / ####'"
            type="text"
            v-model.lazy.trim="v$.partner.newPartner.DOB.$model"
            key="dateOfBirth"
            maxlength="14"
            :isDateInput="true"
            :error="v$.partner.newPartner.DOB.$error"
            :errorMessage="getDOBErrorMessage(v$.partner.newPartner.DOB)"
            :disabled="partner.newPartner.isDuplicate || apiStatus.duplicatePersonCheck.loading"
            @input="searchDebounce(runDuplicatePersonSearch)"
            @blur="runDuplicatePersonSearch()"
            required
          />
          <!-- Duplicate person stuff -->
          <div class="form-grid">
            <ApiLoading v-if="partner.newPartner.duplicateCheckFailed" :apiError="partner.newPartner.duplicateCheckFailed">
              <p>
                Sorry, we couldn’t load the information.
                <a @click="runDuplicatePersonSearch()" class="always-fresh">Reload</a>
                the result to continue
              </p>
            </ApiLoading>
          </div>
          <!-- Duplicate alert message-->
          <FormField v-if="partner.newPartner.isDuplicate">
            <Alert alertType="error" class="fr-view" :message="DUPLICATE_PERSON_ERROR_MESSAGE" :isPlainText="false"></Alert>
          </FormField>
          <!-- Phone number -->
          <template v-if="!partner.newPartner.isDuplicate && !partner.newPartner.duplicateCheckFailed">
            <ContactInfo
              :key="partner.newPartner.preferredContactMethodsValid"
              :initialState="partner.newPartner.preferredContactMethods"
              :getRequired="true"
              :phoneNumberTypeList="informationReferenceData.phoneTypeOptions"
              :labelText="'Preferred phone number'"
              id="partner-phone"
              @updatePreferredContactMethods="updatePreferredContactMethods"
            />
          </template>
          <!-- Email & confirm email -->
            <FormField
              v-if="!partner.newPartner.isDuplicate && !partner.newPartner.duplicateCheckFailed"
              labelText="Email address"
              key="newPartnerEmail"
              type="text"
              class="form-section"
              placeholder="info@defencehealth.com.au"
              required
              v-model.lazy="v$.partner.newPartner.email.$model"
              :error="v$.partner.newPartner.email.$error"
              :errorMessage="getEmailErrorMessage()"
            />
            <FormField v-if="!partner.newPartner.isDuplicate && !partner.newPartner.duplicateCheckFailed" class="form-section">
              <FormLabel labelText="Confirm email address" required forId="confirm-email" />
              <FormInput
                type="text"
                placeholder="info@defencehealth.com.au"
                required
                id="confirm-email"
                v-model.lazy="v$.partner.newPartner.confirmEmail.$model"
                :overrideClass="v$.partner.newPartner.confirmEmail.$error ? 'error' : null"
              />
              <label
                for="confirm-email"
                class="error"
                v-if="v$.partner.newPartner.confirmEmail.$error & v$.partner.newPartner.confirmEmail.IS_INVALID.$invalid && v$.partner.newPartner.confirmEmail.required"
              >
                {{ EmailErrorMessage.NOT_MATCH }}
              </label>
              <label for="confirm-email" class="error" v-if="v$.partner.newPartner.confirmEmail.$error & v$.partner.newPartner.confirmEmail.required.$invalid">
                {{ EmailErrorMessage.IS_EMPTY }} Confirm email address
              </label>
            </FormField>
        </template>
      </SectionSeparatorWrapper>

      <!-- Other sections for new partner info -->
      <template v-if="partner.newPartner.add && showNewPartnerSection && compositionAllowsPartner">
        <!-- New partner - Address -->
        <SectionSeparatorWrapper v-if="partner.newPartner.add && !partner.newPartner.isDuplicate && !partner.newPartner.duplicateCheckFailed" :separator="false" class="field">
          <div class="form-section">
            <AddressValidator
              @updateAddressDetails="updateAddressDetail"
              @updateAddressValidation="updateAddressValidation"
              :isPartner="true"
              :stateLocked="true"
              :postalAddressHelpText="referenceData.postalAddressHelpText.informationText"
              showPostalAddressHelperText
            ></AddressValidator>
          </div>
        </SectionSeparatorWrapper>
        <!-- **************************************************************************************** -->
        <!-- Health insurance history only show for new partner-->
        <template v-if="partner.newPartner.add && !partner.newPartner.isDuplicate && !partner.newPartner.duplicateCheckFailed">
          <HealthInsuranceHistorySection
            @updateEligibleForLHC="updateEligibleForLHC"
            @abdLhcUpdated="getABDAndLHCResponse"
            :eligibleForABD="eligibleForABD && partner.newPartner.add"
            :products="formattedProductPolicies"
            :refData="informationReferenceData"
            :coverStartDate="reverseFormatFormDate(getCurrentDate())"
            :dob="partner.newPartner.DOB"
            :initialData="healthInsuranceHistoryInitialData"
            parentClass="parent"
            :doNotDisplayLhc="doNotDisplayLHC"
            isChangeCover
          />
        </template>

        <!-- Transferring from another fund -->
        <SectionSeparatorWrapper v-if="partner.newPartner.add && !partner.newPartner.isDuplicate && !partner.newPartner.duplicateCheckFailed" class="field" :separator="false">
          <h3 class="h6 sub-heading-underlined">Transferring from another fund</h3>
          <div class="form-section">
            <FormField labelText="Are you transferring from another fund?" :helperText="transferringFundsSupportText">
              <ul class="radio-group large">
                <li v-for="option in displayRadioOptions" :key="`from-other-funds-partner-${option.display}`">
                  <input
                    type="radio"
                    :id="`from-other-funds-partner-${option.display}`"
                    v-model.lazy="partner.newPartner.transferFromOthers"
                    :name="`from-other-funds-partner-${option.display}`"
                    :value="option.returnValue"
                    :disabled="partnerCurrentlyHoldsCover"
                  />
                  <!-- @change="!partner.newPartner.transferFromOthers && resetTransferFundDetails()" -->
                  <label :for="`from-other-funds-partner-${option.display}`">{{ option.display }}</label>
                </li>
              </ul>
            </FormField>
          </div>
          <template v-if="partner.newPartner.transferFromOthers">
            <div class="form-section" v-for="(fund, index) in partner.newPartner.selectedInsuranceFund" :key="index">
              <FormField labelText="Which fund are you transferring from?" required class="form-section">
                <DropdownSelect
                  :items="fundNamesNewPerson"
                  v-model="fund.name"
                  name="partner-fund-name"
                  :id="`partner-new-fundName-${index}`"
                  :initialSelect="getDropdownOption(fundNamesNewPerson, fund.name, 'Please select a health fund')"
                />
              </FormField>
              <FormField
                labelText="What was your member number at your previous health fund?"
                placeholder="XXXXXXXXX"
                type="text"
                class="form-section"
                :id="fund.number"
                v-model.lazy="fund.number"
                required
                :error="v$.partner.newPartner.selectedInsuranceFund.$each.$response?.$errors?.[index]?.number?.length > 0"
                :errorMessage="getFundErrorMessage(v$.partner.newPartner.selectedInsuranceFund.$each.$response?.$errors?.[index]?.number || [])"
              />
              <FormField>
                <p v-html="informationReferenceData.insuranceFundInformationText.informationText" />
              </FormField>

              <div v-if="partner.newPartner.selectedInsuranceFund.length > 1" class="form-section">
                <a @click.prevent="removeSelectField(partner.newPartner.selectedInsuranceFund, index)" class="always-fresh" href="#" role="button">Remove fund</a>
              </div>
            </div>

            <div class="form-section">
              <a
                v-if="partner.newPartner.selectedInsuranceFund.length < 2"
                @click.prevent="addAnotherSelectField(partner.newPartner.selectedInsuranceFund)"
                role="button"
                class="always-fresh"
                href="#"
              >
                Add another fund to switch from
              </a>
            </div>
          </template>
        </SectionSeparatorWrapper>

        <!-- Veteran card -->
        <SectionSeparatorWrapper
          v-if="showVeteranCardSection && partner.newPartner.add && !partner.newPartner.isDuplicate && !partner.newPartner.duplicateCheckFailed"
          :separator="false"
        >
          <div class="form-section">
            <h3 class="h6 sub-heading-underlined">Veteran card</h3>
            <FormField labelText="Do you have a Veteran card?" :helperText="informationReferenceData.additionalInformationUpdateVeteranDetails.informationText">
              <ul class="radio-group large">
                <li v-for="option in displayRadioOptions" :key="`partner-veteran-card-${option.display}`">
                  <input
                    type="radio"
                    :id="`partner-veteran-card-${option.display}`"
                    v-model.lazy="partner.newPartner.veteranCard.haveVeteranCard"
                    :name="`partner-veteran-card-${option.display}`"
                    :value="option.returnValue"
                  />
                  <!-- @change="haveVeteranCardUpdated" -->
                  <label :for="`partner-veteran-card-${option.display}`">{{ option.display }}</label>
                </li>
              </ul>
            </FormField>
          </div>
          <FormField
            v-if="partner.newPartner.veteranCard.haveVeteranCard"
            :required="partner.newPartner.veteranCard.haveVeteranCard"
            labelText="Which Veteran card do you hold?"
            class="form-section"
          >
            <ul class="radio-group large veteran-card-options">
              <li v-for="option in informationReferenceData.veteranCardTypeOptions" :key="option.returnValue">
                <input
                  type="radio"
                  :id="option.returnValue"
                  :value="option.returnValue"
                  name="veteran-card-partner"
                  v-model.lazy="v$.partner.newPartner.veteranCard.veteranCardType.$model"
                />
                <!-- @change="veteranCardTypeUpdated" -->
                <label :for="option.returnValue">{{ option.display }}</label>
              </li>
            </ul>
            <p v-if="v$.partner.newPartner.veteranCard.veteranCardType.$model === 'Veteran Gold Card'">
              {{ informationReferenceData.partnerVeteranCardGoldText.informationText }}
            </p>
            <template v-if="v$.partner.newPartner.veteranCard.veteranCardType.$model === 'Veteran White Card'">
              <p v-html="informationReferenceData.partnerVeteranCardWhiteText.informationText"></p>
              <MultiSelect
                name="white card options"
                id="veteran-card-condition-options-partner"
                :items="informationReferenceData.veteranCardConditionOptions"
                :selectedItems="partner.newPartner.veteranCard.veteranCardConditionOptions"
                placeholderText="Search for a listed accepted condition"
                ref="partner.veteranCardConditionOptions"
                :maximum="informationReferenceData.veteranCardConditionOptions.length"
                @updateData="updateVeteranCardOptions"
              />
            </template>
          </FormField>
        </SectionSeparatorWrapper>

        <!-- Concession card -->
        <SectionSeparatorWrapper
          v-if="partner.newPartner.add && showConcessionCardSection() && !partner.newPartner.isDuplicate && !partner.newPartner.duplicateCheckFailed"
          :separator="false"
        >
          <h3 class="h6 sub-heading-underlined">Concession card</h3>
          <div class="form-section">
            <FormField labelText="Do you have a concession card?">
              <ul class="radio-group large">
                <li v-for="option in displayRadioOptions" :key="`partner-concession-card-${option.display}`">
                  <input
                    type="radio"
                    :id="`partner-concession-card-${option.display}`"
                    v-model.lazy="partner.newPartner.haveConcessionCard"
                    :name="`partner-concession-card-${option.display}`"
                    :value="option.returnValue"
                  />
                  <!-- @change="!partner.haveConcessionCard" -->
                  <label :for="`partner-concession-card-${option.display}`">{{ option.display }}</label>
                </li>
              </ul>
            </FormField>
          </div>
          <template v-if="showConcessionCardDetailsForm()">
            <div class="form-section" v-for="(concession, index) in partner.newPartner.selectedConcessionCardOptions" :key="index">
              <FormField>
                <p>
                  {{ informationReferenceData.partnerConcessionHelperText.informationText }}
                </p>
              </FormField>
              <FormField class="form-section" labelText="Which concession card do you hold?">
                <DropdownSelect
                  initialSelect="Please select a concession card"
                  :items="getSelectOptionsLeft(index, partner.newPartner.selectedConcessionCardOptions, informationReferenceData.concessionCardOptions)"
                  v-model="concession.name"
                />
              </FormField>
              <FormField
                labelText="Card number"
                placeholder="XXXXXXXXX"
                type="text"
                class="form-section"
                v-model.lazy="concession.number"
                :error="v$.partner.newPartner.selectedConcessionCardOptions.$each.$response?.$errors?.[index]?.number?.length > 0"
                :errorMessage="getConcessionNumberErrorMessage(v$.partner.newPartner.selectedConcessionCardOptions.$each.$response?.$errors?.[index]?.number || [])"
              />
              <FormField
                labelText="Expiry date"
                type="text"
                class="form-section half-width"
                v-model.lazy="concession.expire"
                placeholder="DD / MM / YYYY"
                v-maska="'## / ## / ####'"
                :required="false"
                :error="v$.partner.newPartner.selectedConcessionCardOptions.$each.$response?.$errors?.[index]?.expire?.length > 0"
                :errorMessage="getConcessionExpiryErrorMessage(v$.partner.newPartner.selectedConcessionCardOptions.$each.$response?.$errors?.[index]?.expire || [])"
              />
              <div v-if="partner.newPartner.selectedConcessionCardOptions.length > 1" class="form-section">
                <a @click.prevent="removeConcessionSelectField(partner.newPartner.selectedConcessionCardOptions, index)" class="always-fresh" role="button" href="#">Remove card</a>
              </div>
              <div class="form-section">
                <a
                  v-if="partner.newPartner.selectedConcessionCardOptions.length < informationReferenceData.concessionCardOptions.length"
                  @click.prevent="addAnotherSelectField(partner.newPartner.selectedConcessionCardOptions, true)"
                  role="button"
                  class="always-fresh"
                  href="#"
                >
                  Add another concession card
                </a>
              </div>
            </div>
          </template>
        </SectionSeparatorWrapper>
      </template>
    <FormField v-if="!compositionAllowsPartner">
      <Alert :message="PARTNER_NOT_SUPPORTED" :isPlainText="false"></Alert>
    </FormField>
  </div>
</template>

<!-- eslint-disable import/no-extraneous-dependencies -->
<script>
import { email, helpers, maxLength, required, requiredIf } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { DATE_VALIDATOR, JOIN_NOW_NAME_VALIDATOR } from '@general/js/validators';
import { Alert, ApiLoading, FormField, FormInput, FormLabel, MemberTag, SectionSeparatorWrapper } from '@/components';
import { AddressValidator, ContactInfo, DropdownSelect, MultiSelect, Spinner } from '@general/js/components';
import HealthInsuranceHistorySection from '@general/js/templates/JoinNow/JoinNowSteps/FormSection/HealthInsuranceHistory.vue';
import {
  invalidConcessionCardNumber,
  getCurrentDate,
  formatFormDate,
  isPastDate,
  isPastDateIncludingToday,
  overAge,
  stringWithInvalidSpace,
  stringWithSymbols,
  underAge,
  isEmptyString,
  reverseFormatFormDate,
  isPOBoxAddress,
} from '@general/js/utils';
import {
  showConcessionCardSection as showConcessionCard,
  showConcessionCardDetailsForm as showConcessionCardDetails,
  sortByObjectProperty,
  formatPhoneNumber,
  getProductName,
  getPartnerDetailsMP,
  findPolicyHolder
} from '@/utils';
import { ConcessionCardTypes, OperationType } from '@/enums';
import { CoverComposition, RelationshipValue, LHCOverruleReasons } from '@general/js/enums';
import { EmailErrorMessage, NameErrorMessage } from '@general/js/enums/ErrorMessageEnum';
import AddRemovePersonMixins from './AddRemovePersonMixins';

let triggerDuplicateSearch;
const apiTriggerTime = 1000;

export default {
  name: 'Partner',

  setup() { return { v$: useVuelidate() }; },

  mixins: [AddRemovePersonMixins],

  components: {
    AddressValidator,
    Alert,
    ApiLoading,
    ContactInfo,
    DropdownSelect,
    FormField,
    FormInput,
    FormLabel,
    MemberTag,
    MultiSelect,
    SectionSeparatorWrapper,
    Spinner,
    HealthInsuranceHistorySection,
  },

  props: {
    informationReferenceData: {
      type: Object,
      required: true,
    },
    terminationReasons: {
      type: Object,
      required: true,
    },
    // Fund names using `dhl_hiccode` when adding a new partner
    fundNamesNewPerson: {
      type: Array,
      required: true,
    },
    // Fund names using `dhl_code` when terminating a partner
    fundNamesTerminated: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      partner: {
        existing: {
          keepAdult: true,
          acknowledgedEligibilityChange: null,
        },
        newPartner: {
          add: null,
          firstName: '',
          middleName: '',
          lastName: '',
          gender: '',
          DOB: '',
          preferredContactMethods: [],
          preferredContactMethodsValid: false,
          email: '',
          confirmEmail: '',
          transferFromOthers: null,
          selectedInsuranceFund: [{ name: '', number: '' }],
          isDuplicate: false,
          duplicateCheckFailed: false,
          addressesInvalid: false,
          veteranCard: {
            haveVeteranCard: null,
            veteranCardType: '',
            veteranCardConditionOptions: [],
          },
          haveConcessionCard: null,
          selectedConcessionCardOptions: [{ name: '', number: '', expire: '', op: OperationType.ADD, startDate: reverseFormatFormDate(getCurrentDate()) }],
          postalAddress: {},
        },
      },
      compositionAllowsPartner: null,
      mustHavePartner: null,
      policyHolder: {},
      eligibleForLHC: false, // default
      abdLhcResponseValid: false, // default
      partnerCurrentlyHoldsCover: false,
      displayRadioOptions: [
        { returnValue: true, display: 'Yes' },
        { returnValue: false, display: 'No' },
      ],
      displayRadioOptionsPartnerQuestion: [
        { returnValue: true, display: "Yes, I'm adding a new partner" },
        { returnValue: false, display: "No, I'm not adding a partner" },
      ],
      isActive: false,
      cardDetailUpdatedType: {
        TYPE: 'cardType',
        NUMBER: 'cardNumber',
        EXPIRY_DATE: 'expiryDate',
        VETERAN_TYPE: 'veteranType',
        ACCEPTED_CONDITION: 'acceptedCondition',
      },
      EmailErrorMessage,
    };
  },

  beforeMount() {
    this.extractPartnerAndChildren();
    this.coverCompositionPartnerPermissions();

    // Pre-fill data if coming back from Confirmation step
    if (this.partnerData.existing?.keepAdult != null) {
      this.partner.existing = { ...this.partnerData.existing };
    }
    if (this.partnerData.newPartner?.add != null) {
      this.partner.newPartner = { ...this.partnerData.newPartner };
    }
  },

  activated() {
    this.isActive = true;
    this.v$.$touch();
  },

  deactivated() {
    this.isActive = false;
  },

  watch: {
    v$: {
      handler(newData) {
        if (this.isActive) {
          this.$emit('formUpdated', {
            response: this.partner,
            isInvalid:
              newData.$invalid ||
              newData.$error ||
              (!this.partner.keepAdult && (this.partner.newPartner.isDuplicate || this.partner.newPartner.duplicateCheckFailed)) ||
              (!this.isPolicyHolderEligible && !this.partner.existing.keepAdult && !this.partner.existing.acknowledgedEligibilityChange) ||
              this.mustHavePartnerValidationFailed,
          });
        }
      },
      deep: true,
    },
  },

  validations() {
    return this.validationObj;
  },

  computed: {
    terminationReasonsMemberList() {
      const memberTerminationReasonOptions = [...this.terminationReasons.memberReasons, ...this.terminationReasons.bothReasons];
      return sortByObjectProperty(memberTerminationReasonOptions, 'description').reverse();
    },

    validationObj() {
      const partnerObj = {
        partner: {
          existing: {},
          newPartner: {},
        },
      };

      if (this.oldCompositionHasPartner) {
        partnerObj.partner = {
          existing: {
            keepAdult: {
              required,
            },
            acknowledgedEligibilityChange: {
              required: requiredIf(function () {
                return !this.partner.existing.keepAdult && !this.isPolicyHolderEligible;
              }),
            },
            terminationReason: {
              required: requiredIf(function () {
                return !this.partner.existing.keepAdult;
              }),
            },
            fundName: {
              required: requiredIf(function () {
                if (!this.partner.existing.keepAdult) {
                  if (
                    this.transferToFundValues.includes(this.partner.existing.terminationReason) &&
                    (this.partner.existing.fundName === null || this.partner.existing.fundName === undefined)
                  ) {
                    return true;
                  }
                }
                return false;
              }),
            },
          },
          newPartner: {
            add: {
              required: requiredIf(function () {
                return !this.partner.existing.keepAdult;
              }),
            },
          },
        };
      } else {
        // No existing partner - adding a new partner
        partnerObj.partner.newPartner = {
          add: {
            required,
          },
        };
      }

      if (!this.oldCompositionHasPartner || !this.partner.existing.keepAdult) {
        if (this.partner.newPartner.add) {
          partnerObj.partner.newPartner = {
            firstName: {
              required,
              JOIN_NOW_NAME_VALIDATOR,
              stringWithInvalidSpace,
              maxLength: maxLength(160),
            },
            middleName: {
              JOIN_NOW_NAME_VALIDATOR,
              stringWithInvalidSpace,
              maxLength: maxLength(160),
            },
            lastName: {
              required,
              JOIN_NOW_NAME_VALIDATOR,
              stringWithInvalidSpace,
              maxLength: maxLength(160),
            },
            gender: {
              required,
            },
            DOB: {
              required,
              DATE_VALIDATOR,
              overAge,
              underAge,
              isPastDate,
            },
            email: {
              email,
              required,
            },
            confirmEmail: {
              required,
              IS_INVALID: (value, vm) => {
                return value === vm.email;
              },
            },
            preferredContactMethodsValid: {
              isValidated(value) {
                return value === true;
              },
            },
            selectedInsuranceFund: {
              $each: helpers.forEach({
                name: {
                  required: requiredIf(function () {
                    if (this.partner.newPartner.transferFromOthers === null) {
                      return true;
                    }
                    return this.partner.newPartner.transferFromOthers;
                  }),
                },
                number: {
                  required: requiredIf(function () {
                    if (this.partner.newPartner.transferFromOthers === null) {
                      return true;
                    }
                    return this.partner.newPartner.transferFromOthers;
                  }),
                  stringWithSymbols,
                  maxLength: maxLength(19),
                },
              }),
            },
            isDuplicate: () => {
              if (this.partner.newPartner.isDuplicate) {
                // fail validation by returning false if member is duplicate so that the member cannot proceed with the Change Cover flow
                return false;
              }
              return true;
            },
            veteranCard: {
              veteranCardType: {
                required: requiredIf(function () {
                  if (this.showVeteranCardSection) {
                    // only required if I/partner has/is serving
                    if (this.partner.newPartner.veteranCard.haveVeteranCard === null) {
                      return true;
                    }
                    return this.partner.newPartner.veteranCard.haveVeteranCard;
                  }
                  return false;
                }),
              },
            },
            selectedConcessionCardOptions: {
              $each: helpers.forEach({
                name: {
                  required: requiredIf(function () {
                    if (this.partner.newPartner.add && this.showConcessionCardSection()) {
                      if (this.partner.newPartner.haveConcessionCard === null) {
                        return true;
                      }
                    }
                    return this.showConcessionCardDetailsForm();
                  }),
                },
                number: {
                  required: requiredIf(function () {
                    if (this.partner.newPartner.add && this.showConcessionCardSection()) {
                      if (this.partner.newPartner.haveConcessionCard === null) {
                        return true;
                      }
                    }
                    return this.showConcessionCardDetailsForm();
                  }),
                  invalidConcessionCardNumber: (value, option) => {
                    if (option.name === ConcessionCardTypes.PCC || option.name === ConcessionCardTypes.HCC || option.name === ConcessionCardTypes.CSHC) {
                      // TODO: Add these checks to ConcessionUtils file
                      return invalidConcessionCardNumber(value);
                    }
                    return true;
                  },
                },
                expire: {
                  isPastDate: (value, option) => {
                    if (value !== '' && (option.name === ConcessionCardTypes.PCC || option.name === ConcessionCardTypes.HCC || option.name === ConcessionCardTypes.CSHC)) {
                      // TODO: Add these checks to concessionUtils file
                      return !isPastDateIncludingToday(value);
                    }
                    return true;
                  },
                  IS_INVALID: (value) => {
                    return DATE_VALIDATOR(value);
                  },
                },
              }),
            },
            addressesInvalid: {
              isValid(bool) {
                return bool === false;
              },
            },
          };
          partnerObj.abdLhcResponseValid = {
            required: requiredIf(function () {
              return this.eligibleForABD || this.eligibleForLHC;
            }),
            isValidated(value) {
              if (this.eligibleForABD || this.eligibleForLHC) {
                return value === true;
              }
              return true;
            },
          };
        }
      }

      return partnerObj;
    },

    mustHavePartnerValidationFailed() {
      return this.mustHavePartner && this.partner.newPartner.add === false && !this.partner.existing.keepAdult;
    },

    isRebateApplicantBeingTerminated() {
      return this.isPartnerRebateApplicant && !this.partner.existing.keepAdult;
    },

    transferringFundsSupportText() {
      if (this.partnerCurrentlyHoldsCover) {
        return this.informationReferenceData.partnerLHCSupportText.informationText;
      }

      return this.informationReferenceData.additionalInformationTransferringFundHistory.informationText;
    },
  },

  methods: {
    getCurrentDate,
    reverseFormatFormDate,
    formatPhoneNumber,
    getProductName,
    searchDebounce(fn) {
      clearTimeout(triggerDuplicateSearch);
      triggerDuplicateSearch = setTimeout(async () => {
        await fn();
      }, apiTriggerTime);
    },
    getNameErrorMessage(keyName) {
      if (keyName !== 'middleName') {
        if (this.v$.partner.newPartner[keyName].$error && this.v$.partner.newPartner[keyName].maxLength.$invalid) {
          return NameErrorMessage.IS_OVER_LENGTH;
        }
        if (
          this.v$.partner.newPartner[keyName].$error &&
          (this.v$.partner.newPartner[keyName].JOIN_NOW_NAME_VALIDATOR.$invalid || !stringWithInvalidSpace(this.v$.partner.newPartner[keyName].$model))
        ) {
          return NameErrorMessage.IS_INVALID;
        }
        if (this.v$.partner.newPartner[keyName].$error && this.v$.partner.newPartner[keyName].required.$invalid) {
          if (keyName === 'firstName') return `${NameErrorMessage.IS_EMPTY} First name.`;
          return `${NameErrorMessage.IS_EMPTY} Last name.`;
        }

        return '';
      }
      if (this.v$.partner.newPartner.middleName.$error && this.v$.partner.newPartner.middleName.maxLength.$invalid) {
        return NameErrorMessage.IS_OVER_LENGTH;
      }
      if (this.v$.partner.newPartner.middleName.$error && this.v$.partner.newPartner.middleName.JOIN_NOW_NAME_VALIDATOR.$invalid) {
        return NameErrorMessage.IS_INVALID;
      }
      return '';
    },
    getEmailErrorMessage() {
      if (this.v$.partner.newPartner.email.$error && this.v$.partner.newPartner.email.required.$invalid) {
        return `${EmailErrorMessage.IS_EMPTY} Email address`;
      }
      return EmailErrorMessage.IS_INVALID;
    },
    getFundErrorMessage(errors) {
      if (errors.some(e => e.$validator === 'required')) return 'Please include a response for Member number.';
      if (errors.some(e => e.$validator === 'maxLength' || e.$validator === 'stringWithSymbols')) return 'Please enter a combination of up to 19 letters or numbers.';
      return '';
    },
    updatePreferredContactMethods(data) {
      this.partner.newPartner.preferredContactMethods = data.phoneNumbersArr;
      this.partner.newPartner.preferredContactMethodsValid = !data.isInvalid;
    },
    updateAddressDetail(addressDetail, type, isInvalid) {
      this.partner.newPartner.addressesInvalid = isInvalid;
      this.partner.newPartner.differentPostalAddress = addressDetail.differentPostalAddress;
      if (type === 'residential') {
        this.partner.newPartner.residentialAddress = { ...addressDetail.address };
        if (!addressDetail.differentPostalAddress) {
          this.partner.newPartner.postalAddress = { ...addressDetail.address };
        }
      } else if (type === 'postal') {
        if (addressDetail.differentPostalAddress) {
          this.partner.newPartner.postalAddress = { ...addressDetail.address };
        }
      }
    },
    updateAddressValidation(data) {
      this.partner.newPartner.POBox = data.isPOBox;
      this.partner.newPartner.differentPostalAddress = data.differentPostalAddress;
    },
    updateVeteranCardOptions(data) {
      // this.partner.newPartner.veteranCard.veteranCardConditionOptions = []; // reset conditions
      const acceptedConditions = [];
      data.forEach((d) => {
        acceptedConditions.push({
          id: null, // New accepted conditions that have not yet been submitted to OHI have no ID.
          returnValue: d.returnValue,
          display: this.getDisplayValue(d.returnValue, this.informationReferenceData.veteranCardConditionOptions),
          startDate: getCurrentDate(), // Always set to today's date.
          endDate: null,
          originalEndDate: null,
          op: OperationType.ADD,
        });
      });
      this.partner.newPartner.veteranCard.veteranCardConditionOptions = acceptedConditions;
    },
    getCurrentState() {
      // If new partner has different postal address, return their postal state
      if (this.partner.newPartner.differentPostalAddress) {
        return this.partner.newPartner?.postalAddress?.state;
      }
      // Else return state for membership residential address
      const policyHolder = this.allMembersData.find((member) => {
        if (member.relationship?.name) {
          return member.relationship.name.toLowerCase() === RelationshipValue.POLICY_HOLDER.toLowerCase();
        }
        return false;
      });

      const address = policyHolder.address;
      return address.state;
    },
    showConcessionCardSection() {
      const addressState = this.getCurrentState();
      const extrasOnly = this.coverExtrasOnly;
      const partnerDob = this.partner.newPartner.DOB;

      if (isEmptyString(addressState) || isEmptyString(partnerDob)) {
        return false;
      }
      return showConcessionCard(addressState, partnerDob, extrasOnly);
    },
    showConcessionCardDetailsForm() {
      const addressState = this.getCurrentState();
      const extrasOnly = this.coverExtrasOnly;
      const partnerDob = this.partner.newPartner.DOB;
      const partnerConcessionCard = this.partner.newPartner.haveConcessionCard;

      if (isEmptyString(addressState) || isEmptyString(partnerDob) || isEmptyString(partnerConcessionCard)) {
        return false;
      }
      return showConcessionCardDetails(addressState, partnerDob, extrasOnly, partnerConcessionCard);
    },
    removeConcessionSelectField(selectedOptions, index) {
      let selectedData = selectedOptions;
      if (selectedData.length > 1) {
        selectedData = selectedData.splice(index || selectedOptions.length - 1, 1);
      }
    },
    updateEligibleForLHC(data) {
      this.eligibleForLHC = data;
    },
    getABDAndLHCResponse(data) {
      this.abdLhcResponseValid = !data.invalid;

      if (this.eligibleForABD && this.showNewPartnerSection && this.compositionAllowsPartner) {
        this.partner.newPartner.abd = data.abd;
      }
      this.partner.newPartner.lhc = data.lhc;
      this.partnerCurrentlyHoldsCover = data.lhc?.overruledReason === LHCOverruleReasons.CURRENT_HOSPITAL_COVER;
      if (this.partnerCurrentlyHoldsCover) {
        this.partner.newPartner.transferFromOthers = true;
      }
    },

    extractPartnerAndChildren() {
      if (this.allMembersData) {
        const partner = getPartnerDetailsMP(this.allMembersData, true);
        const policyHolder = findPolicyHolder(this.allMembersData, false, true);

        if (partner) {
          const existingPartnerDetails = {
            keepAdult: true,
            personCode: partner.personCode,
            firstName: partner.firstName,
            middleName: partner.middleName,
            lastName: partner.lastName,
          };
          this.partner.existing = existingPartnerDetails;
        }

        if (policyHolder) {
          const residentialAddress = policyHolder.residentialAddress;

          if (residentialAddress) {
            this.policyHolder.residentialAddress = {
              ...this.getAddress(policyHolder, false, false),
            };
          } else {
            this.policyHolder.residentialAddress = {};
          }

          this.policyHolder.differentPostalAddress = !!policyHolder.address.addressLine1;
          this.policyHolder.POBox = isPOBoxAddress(policyHolder.address.addressLine1);
          if (this.policyHolder.differentPostalAddress) {
            this.policyHolder.postalAddress = this.getAddress(policyHolder, this.policyHolder.differentPostalAddress, this.policyHolder.isPOBox);
          } else {
            this.policyHolder.postalAddress = { ...this.policyHolder.residentialAddress };
          }
        }
      }
    },

    async runDuplicatePersonSearch() {
      // only run if not already loading
      if (!this.apiStatus.duplicatePersonCheck.loading) {
        const firstNameInvalid = this.v$.partner.newPartner.firstName.$invalid;
        const lastNameInvalid = this.v$.partner.newPartner.lastName.$invalid;
        const genderInvalid = this.v$.partner.newPartner.gender.$invalid;
        const dobInvalid = this.v$.partner.newPartner.DOB.$invalid;
        // only run if all four fields are valid
        if (!firstNameInvalid && !lastNameInvalid && !genderInvalid && !dobInvalid) {
          const currentFirstName = this.partner.newPartner.firstName;
          const currentLastName = this.partner.newPartner.lastName;
          const currentGender = this.partner.newPartner.gender;
          const currentDOB = formatFormDate(this.partner.newPartner.DOB);

          let isPersonDuplicate = false;
          try {
            isPersonDuplicate = await this.duplicatePersonSearch(currentFirstName, currentLastName, currentGender, formatFormDate(currentDOB));
            this.partner.newPartner.isDuplicate = isPersonDuplicate;
          } catch (error) {
            logger.log(error);
            this.partner.newPartner.duplicateCheckFailed = true;
            this.partner.newPartner.isDuplicate = false;
          }

          // If duplicate, need to raise a duplicate person work item in CRM
          if (isPersonDuplicate) {
            const payload = {
              firstName: currentFirstName,
              lastName: currentLastName,
              dob: currentDOB,
              sex: currentGender,
              needsAnalysisInfo: {},
            };
            const request = {
              workItemCategory: "Duplicate Person Check Member Portal",
              title: "Duplicate Contact in Change Cover Request",
              payload: { payload },
            };
            // make api call (will need to await if error action is required)
            this.raiseWorkItem(request);
          }
        }
      }
    },

    coverCompositionPartnerPermissions() {
      const compositionAllowsPartner = [CoverComposition.COUPLE, CoverComposition.FAMILY, CoverComposition.FAMILY_PLUS].some((comp) => {
        return this.compositionsAllowedForCurrentProducts.includes(comp);
      });

      const mustHavePartner = ![CoverComposition.SINGLE, CoverComposition.SINGLE_PARENT, CoverComposition.SINGLE_PARENT_PLUS, CoverComposition.CHILDREN_ONLY].some((comp) => {
        return this.compositionsAllowedForCurrentProducts.includes(comp);
      });

      this.compositionAllowsPartner = compositionAllowsPartner;
      this.mustHavePartner = mustHavePartner;

      if (!compositionAllowsPartner) {
        this.partner.newPartner.add = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(section.focusable) {
  & > .separator-wrapper {
    row-gap: 5px;
  }

  .separator-content {
    .form-section:not(:last-child) {
      margin-bottom: rem(25);
    }
  }
}

.box-alert {
  margin-bottom: 0;
}

.veteran-card-options {
  margin-bottom: 2rem;
}

.bottom-margin {
  margin-bottom: 10px;
}

:deep(.box) {
  &.alert:last-child {
    margin-bottom: 0;
  }
}

:deep(.dropdown-select-wrapper) {
  overflow: visible;
}

.loading-screen {
  position: absolute;
  background: $WHITE;
  backdrop-filter: none;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 999999;
  opacity: 1;
}

.form-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  align-items: center;
  top: 50%;
  @include desktop {
    column-gap: $desktop_spacing;
  }

  @include tablet {
    column-gap: $tablet_spacing;
  }

  @include mobile {
    column-gap: $mobile_spacing;
    grid-template-columns: 100%;
    grid-template-rows: auto;
  }
}

:deep(#spinner) {
  text-align: center;
}

:deep(.spinner) {
  div {
    &::after {
      top: 48px;
      left: 38px;
      width: 4px;
      height: 22px;
    }
  }
}

:deep(.modal-table) {
  width: 100%;
  margin-bottom: 0;
  text-align: right;

  tr {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  th,
  td {
    border-bottom: none;
    padding-left: 0;
  }

  .break-long-email {
    word-break: break-all;
  }

  a {
    margin-top: 10px;
  }
}
</style>
